import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import * as S from "./SoftwareSlide.styles";
import { ISlide } from "../../FullStackNetworkingSoftware.types";
import { useTranslation } from "hooks/useTranslation";
import { useResizeDetector } from "react-resize-detector";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import DisplayBezelImgSmall from "./assets/display-small.png";
import DisplayBezelImg from "./assets/display.png";
import IPhoneBezelImg from "./assets/iphone-bezel.png";

const INITIAL_WIDTH = 1388;

interface ISoftwareSlideProps {
  item: ISlide;
  isInView?: boolean;
  onVideoEnded: () => void;
  onStartVideoPlayback: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
}

export const SoftwareSlide = ({
  item,
  isInView,
  onVideoEnded,
  onStartVideoPlayback,
}: Readonly<ISoftwareSlideProps>) => {
  const { t } = useTranslation();
  const { width, ref } = useResizeDetector();

  const displayVideoRef = useRef<HTMLVideoElement>(null);
  const iphoneVideoRef = useRef<HTMLVideoElement>(null);

  useVideoCleanup(displayVideoRef);
  useVideoCleanup(iphoneVideoRef);

  const imageScale = useMemo(() => {
    if (width >= INITIAL_WIDTH) {
      return 1;
    }

    const ratio = width / INITIAL_WIDTH;

    return Number.isNaN(Number(ratio)) ? 1 : Number(ratio.toFixed(3));
  }, [width]);

  const playVideo = useCallback(async (videoRef: HTMLVideoElement) => {
    try {
      videoRef.currentTime = 0;
      await videoRef.play();
    } catch (error) {
      console.error("Error during video playback:", error);
    }
  }, []);

  const pauseVideo = useCallback(async (videoRef: HTMLVideoElement) => {
    try {
      await videoRef?.pause();
    } catch (error) {
      console.error("Error during video pause:", error);
    }
  }, []);

  useEffect(() => {
    const videos = [displayVideoRef.current, iphoneVideoRef.current];

    if (isInView) {
      videos.forEach((video) => playVideo(video));
    }

    return () => {
      videos.forEach((video) => pauseVideo(video));
    };
  }, [ref, isInView, playVideo, pauseVideo]);

  return (
    <S.BackgroundVideoWrapper ref={ref}>
      <S.SlideHeader>
        <S.SlideTitle dangerouslySetInnerHTML={{ __html: t(item.title) }} />
        <S.StyledDescription
          dangerouslySetInnerHTML={{
            __html: t(item.description, {
              downloadsPageLink: `<a href="/download" target="blank" rel="noopener noreferrer">${t("gettingStartedWithUniFi.explore.updates.unifiMobileApp")}</a>`,
              watchVideoCallback: `<a href="${window.location.href}#site-manager-popup">${t(
                "gettingStartedWithUniFi.explore.siteManager.video",
              )}</a>`,
            }),
          }}
        />
      </S.SlideHeader>

      <S.DisplayContainer>
        <S.DisplayBackground>
          <picture>
            <source
              media={BREAKPOINTS.MOBILE_AND_DOWN}
              src={getOptimizedImageUrl(
                absoluteAssetUrl(DisplayBezelImgSmall),
                619,
              )}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(DisplayBezelImg),
                1238,
              )} 2x`}
            />
            <S.StyledDisplay
              alt="Display Pro Bezel"
              src={getOptimizedImageUrl(
                absoluteAssetUrl(DisplayBezelImg),
                1388,
              )}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(DisplayBezelImg),
                2760,
              )} 2x`}
            />
          </picture>

          <S.StyledVideo
            ref={displayVideoRef}
            $scale={imageScale}
            src={item.src}
            muted
            autoPlay={isInView}
            poster={item.poster}
            playsInline
            preload="none"
            onCanPlay={onStartVideoPlayback}
            onEnded={onVideoEnded}
          />

          <S.IphoneWatchWrapper $scale={imageScale}>
            <S.StyledIphoneBezel
              alt="iPhone Bezel"
              src={getOptimizedImageUrl(absoluteAssetUrl(IPhoneBezelImg), 645)}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(IPhoneBezelImg),
                1290,
              )} 2x`}
            />

            <S.Iphone
              ref={iphoneVideoRef}
              muted
              playsInline
              autoPlay={isInView}
              src={item.mobileSrc}
              poster={item.mobilePoster}
              preload="none"
            />
          </S.IphoneWatchWrapper>
        </S.DisplayBackground>
      </S.DisplayContainer>
    </S.BackgroundVideoWrapper>
  );
};
